import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import OpenPlayToolsLayout from '../../components/openplay/OpenPlayToolsLayout';
import ParticipantManagementTab from '../session-management-panel/components/ParticipantManagementTab';
import ParticipantEditModal from '../session-management-panel/components/ParticipantEditModal';
import SessionStatusIndicator from '../../components/ui/SessionStatusIndicator';
import { supabase } from '../../lib/supabaseClient';

const buildMemberKey = (name, phone) => {
  return `${String(name || '').trim()}|${String(phone || '').trim()}`;
};

const mapParticipantsWithMembers = (list, index) => {
  const idx = index instanceof Set ? index : new Set();
  return (Array.isArray(list) ? list : []).map((p) => {
    const key = buildMemberKey(p?.name, p?.phone);
    return {
      ...p,
      isMember: Boolean(p?.isMember) || (key.trim() && idx.has(key)),
    };
  });
};

const normalizeInviteParticipants = (raw, price) => {
  if (!Array.isArray(raw)) return [];
  const basePrice = parseInt(price, 10) || 0;
  try {
    return raw
      .map((p) => {
        const guestCount = parseInt(p?.guestCount ?? p?.guest_count ?? '0', 10) || 0;
        const name = String(p?.name || '').trim();
        const phone = String(p?.phone || '').trim();
        const nickname = String(p?.nickname || p?.name || '').trim();
        const registeredAt = String(p?.registeredAt || p?.registered_at || new Date().toISOString());
        return {
          id: String(p?.id || globalThis.crypto?.randomUUID?.() || String(Date.now())),
          name,
          nickname: nickname || name,
          phone,
          status: String(p?.status || 'pending'),
          payment: String(p?.payment || 'pending'),
          registeredAt,
          skillLevel: String(p?.skillLevel || p?.skill_level || ''),
          guestCount: String(guestCount),
          amount: typeof p?.amount === 'number' ? p.amount : basePrice * (1 + guestCount),
          isMember: Boolean(p?.isMember),
        };
      })
      .filter((p) => p.name && p.phone);
  } catch {
    return [];
  }
};

const labelForType = (t) => {
  const type = String(t || '').trim().toLowerCase();
  if (type === 'lesson') return '레슨';
  if (type === 'training') return '트레이닝';
  return '이벤트';
};

const LessonTrainingDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const sessionId = params?.id;

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const [userId, setUserId] = useState('');

  const [membersIndex, setMembersIndex] = useState(new Set());
  const [editParticipantOpen, setEditParticipantOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);

  const loadMembersIndex = async (uid) => {
    if (!uid) return new Set();
    const { data, error } = await supabase.from('members').select('name, phone').eq('client_id', uid);
    if (error) return new Set();

    const idx = new Set();
    (data || []).forEach((r) => {
      const key = buildMemberKey(r?.name, r?.phone);
      if (key.trim()) idx.add(key);
    });
    return idx;
  };

  const persistParticipants = async (nextParticipants) => {
    if (!sessionId) return;
    if (!userId) throw new Error('로그인이 필요합니다.');

    const { error } = await supabase
      .from('session_invites')
      .update({
        participants: nextParticipants,
        registered: (nextParticipants || [])?.length || 0,
      })
      .eq('id', sessionId)
      .eq('client_id', userId);

    if (error) {
      throw new Error(error?.message || '참가자 정보를 저장하지 못했습니다.');
    }
  };

  useEffect(() => {
    let mounted = true;

    const loadInvite = async () => {
      if (!sessionId) {
        setLoadError('잘못된 접근입니다. 초대장 ID가 없습니다.');
        return;
      }

      setIsLoading(true);
      setLoadError('');
      try {
        const { data: sessionDataAuth } = await supabase.auth.getSession();
        const uid = sessionDataAuth?.session?.user?.id;
        if (!uid) {
          setLoadError('로그인이 필요합니다.');
          return;
        }
        if (!mounted) return;
        setUserId(uid);

        const memberIdx = await loadMembersIndex(uid);
        if (!mounted) return;
        setMembersIndex(memberIdx);

        const { data, error } = await supabase.from('session_invites').select('*').eq('id', sessionId).eq('client_id', uid).maybeSingle();
        if (!mounted) return;

        if (error) {
          setLoadError(error?.message || '이벤트 정보를 불러오지 못했습니다.');
          return;
        }

        if (!data) {
          setLoadError('이벤트 정보를 찾을 수 없습니다.');
          return;
        }

        const rawType = String(data?.event_type || data?.session_type || data?.type || '').trim().toLowerCase();
        if (!['lesson', 'training'].includes(rawType)) {
          setLoadError('레슨/트레이닝 이벤트가 아닙니다.');
          return;
        }

        const normalizedParticipants = normalizeInviteParticipants(data?.participants, data?.price);
        const withMembers = mapParticipantsWithMembers(normalizedParticipants, memberIdx);

        const mapped = {
          id: data?.id,
          token: data?.token,
          title: data?.title,
          date: data?.date ? new Date(data.date).toISOString() : null,
          startTime: data?.start_time,
          endTime: data?.end_time,
          venue: data?.venue,
          capacity: data?.capacity ?? 0,
          status: data?.status,
          eventType: rawType,
          participants: withMembers,
        };

        setSessionData(mapped);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadInvite();

    return () => {
      mounted = false;
    };
  }, [sessionId]);

  const handleEditParticipant = (participant) => {
    setSelectedParticipant(participant || null);
    setEditParticipantOpen(true);
  };

  const handleUpdateParticipant = async (updatedParticipant) => {
    if (!userId) {
      alert('로그인이 필요합니다.');
      return;
    }

    const prevParticipantsSnapshot = sessionData?.participants || [];
    if (prevParticipantsSnapshot.length === 0) {
      alert('참가자 목록을 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const exists = prevParticipantsSnapshot.some((p) => p?.id === updatedParticipant?.id);
    if (!exists) {
      alert('업데이트할 참가자를 찾지 못했습니다. 새로고침 후 다시 시도해주세요.');
      return;
    }

    const nextParticipants = prevParticipantsSnapshot.map((p) => (p?.id === updatedParticipant?.id ? updatedParticipant : p));
    setSessionData((prev) => ({
      ...(prev || {}),
      participants: nextParticipants,
    }));

    try {
      await persistParticipants(nextParticipants);
    } catch (e) {
      setSessionData((prev) => ({
        ...(prev || {}),
        participants: prevParticipantsSnapshot,
      }));
      alert(e?.message || '저장에 실패했습니다.');
    }
  };

  const handleDeleteParticipant = async (participantId) => {
    if (!userId) {
      alert('로그인이 필요합니다.');
      return;
    }

    const prevParticipantsSnapshot = sessionData?.participants || [];
    if (prevParticipantsSnapshot.length === 0) {
      alert('참가자 목록을 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const exists = prevParticipantsSnapshot.some((p) => p?.id === participantId);
    if (!exists) {
      alert('삭제할 참가자를 찾지 못했습니다. 새로고침 후 다시 시도해주세요.');
      return;
    }

    const nextParticipants = prevParticipantsSnapshot.filter((p) => p?.id !== participantId);
    setSessionData((prev) => ({
      ...(prev || {}),
      participants: nextParticipants,
    }));

    try {
      await persistParticipants(nextParticipants);
    } catch (e) {
      setSessionData((prev) => ({
        ...(prev || {}),
        participants: prevParticipantsSnapshot,
      }));
      alert(e?.message || '저장에 실패했습니다.');
    }
  };

  const handleBulkUpdateParticipants = async (nextParticipants) => {
    if (!userId) {
      alert('로그인이 필요합니다.');
      return;
    }

    const prevParticipantsSnapshot = sessionData?.participants || [];
    const base = Array.isArray(nextParticipants) ? nextParticipants : [];
    if (prevParticipantsSnapshot.length > 0 && base.length === 0) {
      alert('참가자 목록이 비어있어 저장할 수 없습니다.');
      return;
    }

    const withMembers = mapParticipantsWithMembers(base, membersIndex);
    setSessionData((prev) => ({
      ...(prev || {}),
      participants: withMembers,
    }));

    try {
      await persistParticipants(withMembers);
    } catch (e) {
      setSessionData((prev) => ({
        ...(prev || {}),
        participants: prevParticipantsSnapshot,
      }));
      alert(e?.message || '저장에 실패했습니다.');
    }
  };

  const handleDownloadRoster = () => {
    const list = Array.isArray(sessionData?.participants) ? sessionData.participants : [];
    if (list.length === 0) {
      alert('다운로드할 참가자가 없습니다.');
      return;
    }

    const escapeHtml = (s) => {
      return String(s ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
    };

    const rows = list
      .map((p, idx) => {
        const statusLabel = p?.status === 'confirmed' ? '확정' : p?.status === 'pending' ? '대기' : p?.status || '';
        const paymentLabel = p?.payment === 'paid' ? '완료' : p?.payment === 'pending' ? '대기' : p?.payment || '';
        const memberLabel = p?.isMember ? '회원' : '게스트';
        return `<tr>` +
          `<td>${idx + 1}</td>` +
          `<td>${escapeHtml(p?.name)}</td>` +
          `<td>${escapeHtml(p?.phone)}</td>` +
          `<td>${escapeHtml(memberLabel)}</td>` +
          `<td>${escapeHtml(statusLabel)}</td>` +
          `<td>${escapeHtml(paymentLabel)}</td>` +
          `<td>${escapeHtml(p?.registeredAt)}</td>` +
        `</tr>`;
      })
      .join('');

    const html = `\ufeff<html><head><meta charset="utf-8" /></head><body>` +
      `<table border="1">` +
      `<thead><tr>` +
      `<th>No</th><th>이름</th><th>전화번호</th><th>회원</th><th>상태</th><th>결제</th><th>신청일</th>` +
      `</tr></thead>` +
      `<tbody>${rows}</tbody>` +
      `</table>` +
      `</body></html>`;

    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const datePart = sessionData?.date ? String(sessionData.date).slice(0, 10) : '';
    const titlePart = String(sessionData?.title || 'roster').replaceAll(' ', '_');
    a.href = url;
    a.download = `${titlePart}_${datePart || 'session'}.xls`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSaveParticipantFromModal = async (updatedParticipant) => {
    if (!userId) throw new Error('로그인이 필요합니다.');

    const name = String(updatedParticipant?.name || '').trim();
    const phone = String(updatedParticipant?.phone || '').trim();

    if (updatedParticipant?.isMember) {
      const { data: existing, error: existingError } = await supabase
        .from('members')
        .select('id')
        .eq('client_id', userId)
        .eq('name', name)
        .eq('phone', phone)
        .maybeSingle();

      if (existingError) {
        throw new Error(existingError?.message || '회원 중복 확인에 실패했습니다.');
      }

      if (existing?.id) {
        alert('이미 등록되어 있습니다.');
      } else {
        const skillLevel = String(updatedParticipant?.skillLevel || '').trim() || '1.0';
        const { error: insertError } = await supabase
          .from('members')
          .insert({
            client_id: userId,
            name,
            phone,
            skill_level: skillLevel,
            membership_type: 'regular',
            status: 'active',
          });

        if (insertError) {
          throw new Error(insertError?.message || '회원 등록에 실패했습니다.');
        }

        const nextIdx = new Set(membersIndex);
        nextIdx.add(buildMemberKey(name, phone));
        setMembersIndex(nextIdx);
      }
    }

    await handleUpdateParticipant(updatedParticipant);
  };

  const registeredCount = sessionData?.participants?.length || 0;
  const shareUrl = useMemo(() => {
    const token = String(sessionData?.token || '').trim();
    if (!token) return '';
    return `${window.location.origin}/public-session-page?token=${token}`;
  }, [sessionData?.token]);

  const handleShare = () => {
    if (!shareUrl) {
      alert('공유할 링크가 없습니다.');
      return;
    }
    navigator.clipboard?.writeText(shareUrl);
    alert('공유 링크가 복사되었습니다.');
  };

  return (
    <OpenPlayToolsLayout userRole="player">
      <main className="pt-6 md:pt-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                  {sessionData?.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm md:text-base text-muted-foreground mb-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Tag" size={16} />
                    <span>{labelForType(sessionData?.eventType)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Calendar" size={16} />
                    <span>
                      {sessionData?.date
                        ? new Date(sessionData.date)?.toLocaleDateString('ko-KR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            weekday: 'long',
                          })
                        : '-'}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={16} />
                    <span>
                      {sessionData?.startTime} - {sessionData?.endTime}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Icon name="MapPin" size={16} />
                    <span>{sessionData?.venue}</span>
                  </div>
                </div>

                <SessionStatusIndicator
                  status={sessionData?.status}
                  capacity={sessionData?.capacity}
                  registered={registeredCount}
                  showTooltip={true}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="default"
                  iconName="Share2"
                  iconPosition="left"
                  onClick={handleShare}
                >
                  공유
                </Button>

                <Button
                  variant="outline"
                  size="default"
                  iconName="ExternalLink"
                  iconPosition="left"
                  onClick={() => {
                    if (!shareUrl) {
                      alert('공개 페이지 링크를 만들 수 없습니다.');
                      return;
                    }
                    window.open(shareUrl, '_blank');
                  }}
                >
                  공개 페이지
                </Button>
              </div>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
            iconPosition="left"
            onClick={() => navigate('/session-list-dashboard/invite')}
            className="mb-4"
          >
            이벤트 목록으로
          </Button>

          {!isLoading && !loadError && sessionData && (
            <ParticipantManagementTab
              sessionData={sessionData}
              onUpdateParticipant={handleUpdateParticipant}
              onDeleteParticipant={handleDeleteParticipant}
              onEditParticipant={handleEditParticipant}
              onBulkUpdateParticipants={handleBulkUpdateParticipants}
              onDownloadRoster={handleDownloadRoster}
            />
          )}
        </div>
      </main>

      <ParticipantEditModal
        isOpen={editParticipantOpen}
        onClose={() => {
          setEditParticipantOpen(false);
          setSelectedParticipant(null);
        }}
        participant={selectedParticipant}
        onSave={handleSaveParticipantFromModal}
      />
    </OpenPlayToolsLayout>
  );
};

export default LessonTrainingDetailPage;
